import { Box, Button, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { ManagerVerificationStatus, TManagersAndRepresentatives } from '../types';
import { useAppDispatch } from '~/store/appDispatch';
import { requestAssetManagerToUpdateData } from '~/store/identification-form/thunks/thunks';
import { useSelector } from 'react-redux';
import UnifiedAccountAccess from './UnifiedAccountAccess';
import { AssetManagerItem } from './AssetManagerItem';
import { EMPTY_ASSET_MANAGER } from '../constants';
import { styles } from '~/modules/identification/styles';
import { getDocumentTemplates } from '~/store/identification-form/thunks/documentThunks';
import { errorMessages } from '~/constants/errorMessages.ts';
import { ERROR_CODES } from '~/constants/errorCodes.ts';
import { setIdentificationError } from '~/store/identification-form/slices';
import { commonStyles } from '~/theme/styles/commonStyles';
import { BaseModal } from '~/components/modals/BaseModal/BaseModal.tsx';
import { selectApplicationId } from '~/store/identification-form/selectors';

interface IAssetManagerListProps {
  handleOpenDeleteModal: (onConfirm: () => void) => void;
}

export const AssetManagerList = ({
  handleOpenDeleteModal,
}: IAssetManagerListProps) => {
  const dispatch = useAppDispatch();
  const applicationId = useSelector(selectApplicationId);
  const { control, setValue } = useFormContext<TManagersAndRepresentatives>();
  const [isRequestModalOpened, setIsRequestModalOpened] = useState(false);
  const [requestAction, setRequestAction] = useState<null | (() => void)>(null);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'assetManagers',
    keyName: 'fieldId',
  });

  const handleOpenModal = useCallback((index: number, managerId: string) => {
    setIsRequestModalOpened(true);
    setRequestAction(() => () => updateRequest(index, managerId));
  }, []);

  const handleRemove = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  const handleAppennd = useCallback(() => {
    append(EMPTY_ASSET_MANAGER);
  }, [append]);

  useEffect(() => {
    if (!fields.length) {
      handleAppennd();
    }
  }, [handleAppennd, fields.length]);

  const updateRequest = async (index: number, managerId: string) => {
    try {
      await dispatch(
        requestAssetManagerToUpdateData({
          applicationId: applicationId as number,
          managerId,
        })
      );
      setValue(
        `assetManagers.${index}.verificationStatus`,
        ManagerVerificationStatus.UPDATE_REQUESTED
      );
    } catch (error) {
      console.error('Помилка при оновленні даних менеджера:', error);
    }
  };

  const handleUpdateRequest = () => {
    requestAction?.();
    setRequestAction(null);
    setIsRequestModalOpened(false);
  };

  const handleDocumentTemplates = async () => {
    const code = 'RESPONSIBLE_PERSONS_LIST';
    const result = await dispatch(
      getDocumentTemplates({ code, applicationId: applicationId as number })
    );

    if (getDocumentTemplates.rejected.match(result)) {
      const errorCode = result.payload?.errorCode;
      if (errorCode === ERROR_CODES.TEMPLATE_NOT_FOUND) {
        dispatch(setIdentificationError({ errorMessage: errorMessages.TEMPLATE_NOT_FOUND }));
      } else {
        dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
      }
    }
  };

  return (
    <>
      <UnifiedAccountAccess handleDocumentTemplates={handleDocumentTemplates} />
      <Typography variant="h5" sx={{ textAlign: 'center' }}>
        Ідентифікаційні дані осіб, які мають право розпоряджатися рахунками (інформація про яких
        внесена до переліку осіб, які мають право розпоряджатися рахунком і підписувати розрахункові
        документи / до картки із зразками підписів) та/або майном
      </Typography>
      <Box sx={commonStyles.columnFlexContainer}>
        {fields.map((manager, index) => (
          <AssetManagerItem
            index={index}
            isAccountHolder={manager.isHead}
            key={manager.fieldId}
            handleUpdateRequest={handleOpenModal}
            append={handleAppennd}
            remove={handleRemove}
            managersListLength={fields.length}
            handleOpenDeleteModal={handleOpenDeleteModal}
            verificationStatus={manager.verificationStatus}
          />
        ))}
      </Box>
      <BaseModal
        open={isRequestModalOpened}
        onClose={() => setIsRequestModalOpened(false)}
        containerProps={{ sx: styles.modalContainer }}
      >
        <Typography sx={{ textAlign: 'center' }}>
          Зазначеному розпоряднику буде відправлено на електронну пошту посилання для оновлення
          даних
        </Typography>
        <Button onClick={handleUpdateRequest} variant="contained">
          Надіслати посилання
        </Button>
      </BaseModal>
    </>
  );
};
